import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import logo from '../assets/images/123votelogo.png';

function useClickOutside(ref: React.RefObject<HTMLElement | null>, handler: () => void) {
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        handler();
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [ref, handler]);
}

export default function NavBar() {
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { pathname } = location;
  const isEmbedMode = new URLSearchParams(location.search).get("embed") === "true";
  const isInIframe = window.self !== window.top;

  const [mobileLoginOpen, setMobileLoginOpen] = useState(false);

  // Popup states
  const [pricingOpen, setPricingOpen] = useState(false);
  const [getStartedOpen, setGetStartedOpen] = useState(false);
  const [loginOpen, setLoginOpen] = useState(false);

  const pricingRef = useRef<HTMLDivElement>(null);
  const getStartedRef = useRef<HTMLDivElement>(null);
  const loginRef = useRef<HTMLDivElement>(null);


  useClickOutside(pricingRef, () => setPricingOpen(false));
  useClickOutside(getStartedRef, () => setGetStartedOpen(false));
  useClickOutside(loginRef, () => setLoginOpen(false));

  if (isEmbedMode 
    || isInIframe 
    || pathname.includes("password") 
    || pathname.includes("admin") 
    || pathname.includes("partner") 
    || pathname.includes("supervisor") 
    || pathname.includes("canvasser"))  {
    return null;
  }

  return (
    <nav className="bg-slate-800 text-white px-6 py-4 shadow-md relative z-50">
      <div className="flex justify-between items-center">
        <Link to="/" className="flex items-center space-x-2">
          <img
            src={logo}
            alt="123-Logo"
            className="h-16 w-auto animate-swing cursor-pointer"
          />
        </Link>


        <div className="hidden md:flex items-center space-x-6 text-sm font-medium">
          <Link to="/about" className="hover:text-gray-400">About</Link>

          <div
            ref={getStartedRef}
            className="relative"
          >
            <button
              onClick={() => setGetStartedOpen(!getStartedOpen)}
              className="hover:text-gray-400 transition-colors duration-200"
            >
              Get Started
            </button>

            <div
              className={`absolute top-full left-0 mt-2 w-64 transform transition-all duration-300 origin-top-left ${getStartedOpen
                  ? 'scale-100 opacity-100 translate-y-0'
                  : 'scale-95 opacity-0 -translate-y-2 pointer-events-none'
                }`}
            >
              <div className="bg-gradient-to-br from-gray-900 to-black border border-gray-800 rounded-2xl shadow-2xl overflow-hidden backdrop-blur-xl">
                <div className="absolute inset-0 bg-gradient-to-br from-blue-600/20 to-purple-600/20"></div>
                <div className="relative p-6">
                  <h3 className="text-white font-bold text-lg mb-4">Get Started</h3>
                  <div className="space-y-3">
                    <button
                      onClick={() => {
                        navigate('/partner/login');
                        setGetStartedOpen(false);
                      }}
                      className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white px-4 py-3 rounded-xl transition-all duration-200 transform hover:scale-105 text-left font-medium flex items-center justify-between group"
                    >
                      <span>Login as Partner</span>
                      <svg className="w-4 h-4 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                    <Link
                      to="/demo"
                      onClick={() => {
                        setGetStartedOpen(false);
                      }}
                      className="w-full block bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-700 hover:to-red-700 text-white px-4 py-3 rounded-xl transition-all duration-200 transform hover:scale-105 font-medium"
                    >
                      Book a Demo
                      
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div
            ref={pricingRef}
            className="relative"
          >
            <button
              onClick={() => setPricingOpen(!pricingOpen)}
              className="hover:text-gray-400 transition-colors duration-200"
            >
              Pricing
            </button>

            {/* Ultra Modern Popup for Pricing */}
            <div
              className={`absolute top-full -left-1/2 mt-2 w-72 transform transition-all duration-300 origin-top-left ${pricingOpen
                  ? 'scale-100 opacity-100 translate-y-0'
                  : 'scale-95 opacity-0 -translate-y-2 pointer-events-none'
                }`}
            >
              <div className="bg-gradient-to-br from-gray-900 to-black border border-gray-800 rounded-2xl shadow-2xl overflow-hidden backdrop-blur-xl">
                <div className="absolute inset-0 bg-gradient-to-br from-green-600/20 to-blue-600/20"></div>
                <div className="relative p-6">
                  <h3 className="text-white font-bold text-lg mb-4">Pricing Plans</h3>
                  <div className="space-y-3">
                    <Link
                      to="/pricing"
                      onClick={() => setPricingOpen(false)}
                      className="block bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white px-4 py-3 rounded-xl transition-all duration-200 transform hover:scale-105 font-medium"
                    >
                      View Pricing Plans
                    </Link>
                    <Link
                      to="/demo"
                      onClick={() => {
                        setPricingOpen(false);
                      }}
                      className="w-full block bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-700 hover:to-red-700 text-white px-4 py-3 rounded-xl transition-all duration-200 transform hover:scale-105 font-medium"
                    >
                      Book a Demo
                    </Link>
                  </div>
                  <div className="mt-4 pt-4 border-t border-gray-700">
                    <p className="text-gray-400 text-sm">Flexible plans for organizations of all sizes</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <Link to="/register-to-vote" className="hover:text-gray-400">Register to Vote</Link>
          

          <div
            ref={loginRef}
            className="relative"
          >
            <button
              onClick={() => setLoginOpen(!loginOpen)}
              className="relative overflow-hidden px-4 py-2 rounded-full text-sm font-semibold text-white bg-red-600 hover:bg-red-700 transition-all duration-200 transform hover:scale-105"
            >
              <span className="relative z-10">Login</span>
            </button>

            <div
              className={`absolute right-0 top-full mt-2 w-56 transform transition-all duration-300 origin-top-right ${loginOpen
                  ? 'scale-100 opacity-100 translate-y-0'
                  : 'scale-95 opacity-0 -translate-y-2 pointer-events-none'
                }`}
            >
              <div className="bg-gradient-to-br from-gray-900 to-black border border-gray-800 rounded-2xl shadow-2xl overflow-hidden backdrop-blur-xl">
                <div className="absolute inset-0 bg-gradient-to-br from-red-600/20 to-orange-600/20"></div>
                <div className="relative p-4">
                  <h3 className="text-white font-bold text-base mb-3">Choose Your Role</h3>
                  <div className="space-y-2">
                    <button
                      onClick={() => {
                        navigate('/canvasser/login');
                        setLoginOpen(false);
                      }}
                      className="w-full bg-gradient-to-r from-blue-600/20 to-blue-700/20 hover:from-blue-600/30 hover:to-blue-700/30 border border-blue-600/50 text-white px-3 py-2 rounded-lg transition-all duration-200 text-left text-sm font-medium flex items-center justify-between group"
                    >
                      <span>As a Canvasser</span>
                      <svg className="w-3 h-3 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                    <button
                      onClick={() => {
                        navigate('/partner/login');
                        setLoginOpen(false);
                      }}
                      className="w-full bg-gradient-to-r from-green-600/20 to-green-700/20 hover:from-green-600/30 hover:to-green-700/30 border border-green-600/50 text-white px-3 py-2 rounded-lg transition-all duration-200 text-left text-sm font-medium flex items-center justify-between group"
                    >
                      <span>As a Partner</span>
                      <svg className="w-3 h-3 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                    <button
                      onClick={() => {
                        navigate('/supervisor/login');
                        setLoginOpen(false);
                      }}
                      className="w-full bg-gradient-to-r from-purple-600/20 to-purple-700/20 hover:from-purple-600/30 hover:to-purple-700/30 border border-purple-600/50 text-white px-3 py-2 rounded-lg transition-all duration-200 text-left text-sm font-medium flex items-center justify-between group"
                    >
                      <span>As a supervisor</span>
                      <svg className="w-3 h-3 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="md:hidden">
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="text-white focus:outline-none"
            aria-label="Toggle menu"
          >
            {isOpen ? '✖' : '☰'}
          </button>
        </div>
      </div>

      {isOpen && (
        <div className="mt-4 md:hidden flex flex-col space-y-3 text-sm font-medium">
          <Link to="/" className="hover:text-gray-400">Home</Link>
          <Link to="/about" className="hover:text-gray-400">About</Link>
          <Link to="/pricing" className="hover:text-gray-400">Pricing</Link>
          <Link to="/demo" className="hover:text-gray-400">Request Demo</Link>
          <Link to="/check-registration" className="hover:text-gray-400">Register to vote</Link>
          {/* <a href="#" className="hover:text-gray-400">Locations</a> */}
          {/* <a href="#" className="hover:text-gray-400">Contact Us</a> */}
          <div className="w-max">
            <button
              onClick={() => setMobileLoginOpen(!mobileLoginOpen)}
              className="relative overflow-hidden px-4 py-2 rounded-full text-sm font-semibold text-white bg-red-600 hover:text-black group w-full"
            >
              <span className="relative z-10 text-white">Get Started</span>
              <span className="absolute inset-0 bg-gradient-to-r from-white via-black to-blue-600 opacity-0 group-hover:opacity-100 animate-gradient-move transition-all duration-700 blur-sm"></span>
            </button>
            {mobileLoginOpen && (
              <div className="mt-2 bg-[#ffffff40] text-black rounded shadow-md py-1 w-56">
                <button
                  onClick={() => navigate('/canvasser/login')}
                  className="w-full text-left px-4 py-2 hover:bg-gray-100"
                >
                  As a Canvasser
                </button>
                <button
                  onClick={() => navigate('/partner/login')}
                  className="w-full text-left px-4 py-2 hover:bg-gray-100"
                >
                  As a Partner
                </button>
                <button
                  onClick={() => navigate('/admin/login')}
                  className="w-full text-left px-4 py-2 hover:bg-gray-100"
                >
                  As Admin
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="h-[10px] bg-white w-full mt-4"></div>
    </nav>
  );
}
